import React from 'react';
import useStorefrontTheme from '../../hooks/useStorefrontTheme';

/**
 * Storefront light/dark toggle. Single icon button — sun in dark mode,
 * moon in light. Click flips and pins the choice; no settings popup,
 * no glow, no gradient. The button inherits `currentColor` so it slots
 * cleanly into the header alongside the cart and currency icons.
 *
 * Styling lives in `styles/storefrontTheme.css` (`.sf-theme-toggle`) so
 * dark mode can recolor the hover state via `--sf-*` tokens.
 */
const ThemeToggle = ({ className = '', style }) => {
  const { resolvedTheme, toggle, canToggle } = useStorefrontTheme();
  const isDark = resolvedTheme === 'dark';

  // The merchant controls whether visitors may override the storefront's
  // default appearance. Omit the button entirely when that option is off.
  if (!canToggle) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-pressed={isDark}
      title={isDark ? 'Light mode' : 'Dark mode'}
      className={`sf-theme-toggle ${className}`.trim()}
      style={style}
    >
      {isDark ? (
        // Sun
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2" />
          <path d="M12 20v2" />
          <path d="m4.93 4.93 1.41 1.41" />
          <path d="m17.66 17.66 1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="m6.34 17.66-1.41 1.41" />
          <path d="m19.07 4.93-1.41 1.41" />
        </svg>
      ) : (
        // Moon
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
};

export default ThemeToggle;
